import {
  ChangeDetectorRef,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewRef,
  ChangeDetectionStrategy
} from "@angular/core";
import { getApp } from "../app";
import { NavigationEnd, Router } from "@angular/router";
import { Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { filterInitialState } from "../filter-list/filter-list.state";
import { Customer } from "../../../../shared/types/customers";
import { CustomerName } from "../../../../shared/config/customers";

@Component({
    selector: "app-sidebar",
    templateUrl: "./sidebar.component.html",
    styleUrls: ["./sidebar.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SidebarComponent implements OnInit, OnDestroy {
  private readonly rmDataHandlingStateKey = "lcm-rm-data-handling-open";
  private readonly omDataHandlingStateKey = "lcm-om-data-handling-open";
  @ViewChild("searchField", { static: false }) searchField: any;
  @ViewChild("dropdownItem", { static: false }) dropdownItem: any;
  public unreadCount = 0;
  customers: string[] = [];
  cleanCustomers: string[] = [];
  searchedClient = "";
  logoLoadFailed = false;
  customerMenuOpen = false;
  rmDataHandlingExpanded = this.readExpandedState(this.rmDataHandlingStateKey);
  omTasksExpanded = this.readExpandedState(this.omDataHandlingStateKey);

  app = getApp((app) => {
    this.app = app;
  });

  private _unreadSubscription: Subscription = new Subscription();
  private _profilePictureSubscription: Subscription = new Subscription();
  private _modeSubscription: Subscription = new Subscription();
  private _navigationSubscription: Subscription = new Subscription();

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router
  ) {}

  public async ngOnInit() {
    // get unread count if users have access to the alerts (all user roles except guest)
    this._unreadSubscription = this.app.mail.updateUnreadCount.subscribe(
      async (value) => {
        if (value) {
          await this.app.change.getUnreadChanges();
          await this.app.pcns.getUnreadPcns();
          await this.app.alerts.getUnreadAlerts();
          this.unreadCount = this.app.mail.unread;
          if (!(this.cdRef as ViewRef).destroyed) {
            this.cdRef.detectChanges();
          }
        }
      }
    );
    this.app.auth.getCurrentUserRole();

    const roleIndex = this.app.auth.rolesArray.findIndex(
      (role: string) => role === `${this.app.customers.expectCurrent}-team`
    );
    if (roleIndex !== -1) {
      this.app.auth.isTeam = true;
    }

    this._profilePictureSubscription =
      this.app.profile.ownProfilePictureChanges.subscribe((value) => {
        if (value !== "") {
          this.app.profile.ownProfilePicture = value;
        } else {
          this.app.profile.ownProfilePicture = null;
        }
        this.cdRef.detectChanges();
      });

    if (this.app.profile.ownProfilePicture == null) {
      await this.app.profile.getProfilePicture(this.app.state.userId);
    }

    this._modeSubscription = this.app.header.newHeaderSubject.subscribe(
      async (value) => {
        this.app.header.showSidebar = value;
        this.cdRef.detectChanges();
      }
    );

    this._navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.openSubcategoryForCurrentView());

    this.cleanCustomers = this.app.customers.customers;
    this.customers = this.app.customers.customers;

    this.openSubcategoryForCurrentView();

  }

  routerLinkUrl(route: string) {
    // special case for analyst and RMAdmin role - navigate to alerts table
    if (
      route === "mail/change" &&
      (this.app.state.isAnalyst || this.app.state.isRMAdmin)
    ) {
      return `/${this.app.customers.expectCurrent}/` + "mail/alert";
    }
    if (route === "profile") {
      return (
        `/${this.app.customers.expectCurrent}/` +
        "profile/" +
        this.app.state.userId
      );
    }
    return `/${this.app.customers.expectCurrent}/` + route;
  }

  navigateRM() {
    /** created only to take out several functions call in the html */
    this.app.routing.navigateRM();
    this.app.paginator.focus = 0;
    this.app.tree.partsOfCurrentAssembly = [];
    this.app.tree.partsOfSearchedAssembly = [];
    this.app.filterTree.assemblySelected = "";
    this.app.filterTree.resetFilterTree();
  }

  navigateRMSearch() {
    /** created only to take out several functions call in the html */
    this.app.routing.navigateRMSearch();
    this.app.paginator.focus = 0;
    this.app.RM.filterOpen = false;
  }

  resetFilters() {
    this.app.filterList.open = false;
    this.app.state.filters = {};
    const initialState = filterInitialState;
    initialState.filters = {};
    this.app.state.next({ ...initialState });
    this.app.filterTree.resetFilterTree();
    this.app.filterTree.resetFiltersByPage();
  }

  navigateUserProfile() {
    if (this.app.state.userId == null) {
      return;
    }
    this.app.routing.navigateProfile(this.app.state.userId);
  }

  onLogoError(): void {
    this.logoLoadFailed = true;
  }

  toggleCustomerMenu(event: Event): void {
    event.stopPropagation();
    this.customerMenuOpen = !this.customerMenuOpen;
  }

  @HostListener("document:click", ["$event"])
  closeCustomerMenu(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.customerMenuOpen && target.closest("#customer-menu, .customer-switcher") == null) {
      this.customerMenuOpen = false;
    }
  }

  selectCustomer(customer: CustomerName): void {
    this.customerMenuOpen = false;
    this.resetFilters();
    this.app.routing.navigateCustomer(customer);
  }

  toggleRmDataHandling(event: Event): void {
    event.stopPropagation();
    this.rmDataHandlingExpanded = !this.rmDataHandlingExpanded;
    this.persistExpandedState(
      this.rmDataHandlingStateKey,
      this.rmDataHandlingExpanded
    );
  }

  toggleOmTasks(event: Event): void {
    event.stopPropagation();
    this.omTasksExpanded = !this.omTasksExpanded;
    this.persistExpandedState(this.omDataHandlingStateKey, this.omTasksExpanded);
  }

  navigateOmModule(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.omTasksExpanded = true;
    this.persistExpandedState(this.omDataHandlingStateKey, true);
    this.app.routing.navigateHome();
    this.app.paginator.focus = 0;
  }

  navigateRmModule(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.rmDataHandlingExpanded = true;
    this.persistExpandedState(this.rmDataHandlingStateKey, true);
    this.navigateRM();
  }

  private openSubcategoryForCurrentView(): void {
    const omSubcategoryActive =
      this.app.view === "home" ||
      this.app.view === "tasks";
    const rmSubcategoryActive =
      this.app.view === "RM" ||
      this.app.view === "RMSearch" ||
      this.app.view === "external-data-filter" ||
      this.app.view === "operations" ||
      this.app.view === "business-config";

    if (omSubcategoryActive) {
      this.omTasksExpanded = true;
      this.persistExpandedState(this.omDataHandlingStateKey, true);
    } else if (rmSubcategoryActive) {
      this.rmDataHandlingExpanded = true;
      this.persistExpandedState(this.rmDataHandlingStateKey, true);
    }
  }

  private readExpandedState(key: string): boolean {
    try {
      return localStorage.getItem(key) === "true";
    } catch {
      return false;
    }
  }

  private persistExpandedState(key: string, value: boolean): void {
    try {
      localStorage.setItem(key, String(value));
    } catch {
      // Ignore storage failures and keep the current in-memory state.
    }
  }

  isOmModuleActive(): boolean {
    return (
      this.app.view === "home" ||
      this.app.view === "tasks"
    );
  }

  isRmModuleActive(): boolean {
    return (
      this.app.view === "RM" ||
      this.app.view === "RMSearch" ||
      this.app.view === "part" ||
      this.app.view === "manufacturer" ||
      this.app.view === "external-data-filter" ||
      this.app.view === "business-config"
    );
  }

  showPortalOMButton() {
    if (
      (this.app.customer === Customer.OMP &&
        !this.app.permission.RM.isAnalyst &&
        !this.app.auth.isRMAdmin) ||
      this.app.customer === Customer.COOP
    ) {
      return true;
    }
    return false;
  }

  showOMButton() {
    if (
      this.app.customers.expectCurrent !== Customer.OMP &&
      this.app.customers.expectCurrent !== Customer.COOP &&
      this.app.customers.expectCurrent !== Customer.OC
    ) {
      if (!this.app.permission.RM.isAnalyst && !this.app.auth.isRMAdmin) {
        return true;
      }
    }
    return false;
  }

  filterCustomers(searchedClient: string) {
    this.customers = this.app.customers.customers;
    const customersNames = this.customers.map((customer) =>
      this.app.customers.getText(customer)
    );

    const result = customersNames.filter(
      (element) =>
        String(element).toLowerCase().indexOf(searchedClient.toLowerCase()) !==
        -1
    );

    this.customers = [];
    result.forEach((customer: string) => {
      // Used to convert the client name to the value from the database (navigateCustomer(dbValue))
      const dbValue = this.app.customers.getLowerCaseText(customer);
      this.customers.push(dbValue);
    });
  }

  checkKey(event: KeyboardEvent) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      this.dropdownItem.nativeElement.focus();
    } else if (event.key !== "Enter") {
      this.searchField.nativeElement.focus();
    }
  }

  ngOnDestroy(): void {
    // Set Subject value to false to prevent retrigger as soon as the new Route is loaded
    this.app.mail.updateUnreadCount.next(false);

    if (this._unreadSubscription) {
      this._unreadSubscription.unsubscribe();
    }
    if (this._profilePictureSubscription) {
      this._profilePictureSubscription.unsubscribe();
    }
    if (this._modeSubscription) {
      this._modeSubscription.unsubscribe();
    }
    if (this._navigationSubscription) {
      this._navigationSubscription.unsubscribe();
    }
  }
}
